import { useCallback, useEffect, useState } from 'react';
import { indexedDBService } from '../services/storage/indexedDB';
import { useAuthStore } from '../store/auth';

interface SearchResult {
  key: string;
  namespaceId: string;
  namespaceName?: string;
  value?: any;
}

export function useLocalSearch(searchTerm: string, options: {
  includeValues?: boolean;
  limit?: number;
  enabled?: boolean;
  exactKey?: boolean;
} = {}) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuthStore();
  const { enabled = true, limit = 20, exactKey = false } = options;
  
  const performSearch = useCallback(async () => {
    if (!searchTerm || searchTerm.length < 2 || !isAuthenticated || !enabled) {
      setResults([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Search in IndexedDB
      const keyMatches = await indexedDBService.searchKeys(searchTerm);
      
      // Filter by exact key match if required
      const filteredMatches = exactKey 
        ? keyMatches.filter(match => match.key === searchTerm)
        : keyMatches;
      
      // Limit results
      const limitedResults = filteredMatches.slice(0, limit);
      
      // If we need to include values, fetch them
      if (options.includeValues) {
        const resultsWithValues = await Promise.all(
          limitedResults.map(async (match) => {
            const value = await indexedDBService.getKeyValue(match.namespaceId, match.key);
            return {
              key: match.key,
              namespaceId: match.namespaceId,
              value
            };
          })
        );
        setResults(resultsWithValues);
      } else {
        setResults(limitedResults.map(match => ({
          key: match.key,
          namespaceId: match.namespaceId
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error during search'));
      console.error('Error during local search:', err);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, isAuthenticated, enabled, options.includeValues, limit, exactKey]);
  
  useEffect(() => {
    performSearch();
  }, [performSearch]);
  
  return {
    data: { results },
    isLoading,
    isError: !!error,
    error,
    refetch: performSearch
  };
}
